import React, { useState, useMemo, type RefCallback, useCallback } from 'react';
import AutomationIcon from '@atlaskit/icon/core/automation';
import type { TriggerProps } from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import {
	useEntryPointButtonTrigger,
	type EntryPointButtonTrigger,
} from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useProjectIdOrKey } from '@atlassian/jira-horizontal-nav-common/src/common/utils.tsx';
import { automationMenuContentEntrypoint } from '@atlassian/jira-automation-menu-content/src/entrypoint.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';

export const AutomationButton = () => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const projectIdOrKey = useProjectIdOrKey() ?? '';

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		automationMenuContentEntrypoint,
		useMemo(() => ({ cloudId, projectIdOrKey }), [cloudId, projectIdOrKey]),
	);

	const buttonTrigger = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const handleClose = useCallback(() => setIsOpen(false), []);

	const runtimeProps = useMemo(
		() => ({
			onClose: handleClose,
		}),
		[handleClose],
	);

	const content = useCallback(
		() => (
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="horizontal-nav-header-automation"
				packageName="@atlassian/jira-horizontal-nav-header"
				runtimeProps={runtimeProps}
			/>
		),
		[entryPointReferenceSubject, runtimeProps],
	);

	// move back inline when cleaning up jira-project-theming-m2-bug-fixes
	const iconColor = useMemo(() => {
		if (fg('jira-project-theming-m2-bug-fixes')) {
			return isOpen ? 'currentColor' : token('color.icon.subtle');
		}
		return isVisualRefreshEnabled() ? token('color.icon.subtle') : 'currentColor';
	}, [isOpen]);

	const trigger = useCallback(
		(triggerProps: TriggerProps | { ref: RefCallback<HTMLElement> }) => (
			<Tooltip content={formatMessage(messages.automationButton)} position="top">
				{fg('jfp-magma-stability-horizontal-nav') ? (
					<ThemedButtonStable
						triggerProps={triggerProps}
						buttonTrigger={buttonTrigger}
						setIsOpen={setIsOpen}
						isOpen={isOpen}
						iconColor={iconColor}
					/>
				) : (
					<ThemedButton
						{...triggerProps}
						ref={(element: HTMLElement) => {
							buttonTrigger(element);
							if (typeof triggerProps.ref === 'function') {
								triggerProps.ref(element);
							}
						}}
						onClick={() => {
							const analyticsEvent = createAnalyticsEvent({
								actionSubject: 'automationBolt',
								action: 'opened',
							});

							fireUIAnalytics(analyticsEvent);

							setIsOpen(!isOpen);
						}}
						appearance={isVisualRefreshEnabled() ? 'default' : 'subtle'}
						isSelected={isOpen}
						data-vc="automation-menu-button"
						iconBefore={
							<AutomationIcon
								label={formatMessage(messages.automationButton)}
								color={iconColor}
								spacing="spacious"
							/>
						}
					/>
				)}
			</Tooltip>
		),
		[buttonTrigger, createAnalyticsEvent, formatMessage, iconColor, isOpen],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={handleClose}
			placement="bottom-end"
			messageId="horizontal-nav.ui.content.header.extra-components-far.automation-button.popup"
			messageType="transactional"
			content={content}
			trigger={trigger}
			shouldRenderToParent
		/>
	);
};

const ThemedButtonStable = ({
	triggerProps,
	buttonTrigger,
	setIsOpen,
	isOpen,
	iconColor,
}: {
	triggerProps: TriggerProps | { ref: RefCallback<HTMLElement> };
	buttonTrigger: EntryPointButtonTrigger;
	setIsOpen: (isOpen: boolean) => void;
	isOpen: boolean;
	iconColor: 'currentColor' | 'var(--ds-icon-subtle)';
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const onClick = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			actionSubject: 'automationBolt',
			action: 'opened',
		});

		fireUIAnalytics(analyticsEvent);

		setIsOpen(!isOpen);
	}, [createAnalyticsEvent, isOpen, setIsOpen]);

	const ref = useCallback(
		(element: HTMLElement) => {
			buttonTrigger(element);
			if (typeof triggerProps.ref === 'function') {
				triggerProps.ref(element);
			}
		},
		[buttonTrigger, triggerProps],
	);
	return (
		<ThemedButton
			{...triggerProps}
			ref={ref}
			onClick={onClick}
			appearance={isVisualRefreshEnabled() ? 'default' : 'subtle'}
			isSelected={isOpen}
			data-vc="automation-menu-button"
			iconBefore={
				<AutomationIcon
					label={formatMessage(messages.automationButton)}
					color={iconColor}
					spacing="spacious"
				/>
			}
		/>
	);
};
